/**
 * partner.mahi.fashion — global auth context.
 * Provides { partner, loading, refresh, signOut } to every page.
 *
 * Re-reads /me on mount so a suspended/rejected account takes effect
 * immediately — the JWT's 30-day TTL must not keep a deactivated brand
 * logged in.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import partnerApi from './api';

const Ctx = createContext({ partner: null, loading: true, refresh: () => {}, signOut: () => {} });

export const PartnerAuthProvider = ({ children }) => {
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem('partner_mahi_token')) {
      setPartner(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await partnerApi.get('/me');
      setPartner(data);
      return data;
    } catch (err) { // eslint-disable-line no-unused-vars
      // 401 → api.js interceptor clears token and redirects to /login.
      setPartner(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('partner_mahi_token');
    setPartner(null);
    window.location.href = '/login';
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <Ctx.Provider value={{ partner, loading, refresh, signOut }}>
      {children}
    </Ctx.Provider>
  );
};

export const usePartnerAuth = () => useContext(Ctx);
